/**
 * API редактора Р7 для вложений FILEATTACHMENTS (WUI-305 + WUI-316 + WUI-344).
 *
 * - `prepareOfficeAttachmentEditor` — открытие сессии DS
 * - `parseOfficeCallbackEvent` — разбор WS `OfficeCallbackHandler.statusChanged`
 * - `fetchOfficeAttachmentDocumentSyncState` — timeout fallback (`syncState`)
 * - `fetchOfficeAttachmentForceSave` — DS Command Service через middle
 * - `discardOfficeAttachmentDocument` — отмена save-цикла
 *
 * @see dev/WUI-346.md §6, §7
 */
import api from '@/common/constants/api'
import { axios as axiosInstance } from '@/common/plugins/axios'
import {
  buildAttachmentOfficeEditorDocumentRequest,
  parseOfficeEditorDocumentSyncStateResponse,
  parseOfficeEditorPrepareResponse,
  snapshotAttachmentPrepareRequest,
} from '@/common/utils/fileAttachmentsUniData'
import { logR7AttachmentPrepareDebug } from '@/common/utils/attachmentDebugLog'
import { normalizeOfficeEditorPrepareUrls } from '@/common/utils/officeEditorPublicUrl'
import type { PlatformFileSessionContext } from '@/common/utils/platformFileSession'
import type { R7DocEditorInitConfig } from '@/common/types/r7DocEditorInitConfig'
import { ActionError } from '@/common/exceptions'

export type OfficeEditorPrepareResult = {
  documentKey: string
  documentUrl: string
  callbackUrl: string
  storageUri: string
  documentSessionId: string
  generation: number
  dsDirty: boolean
  /** Init-config DocEditor от middleware (подписываемая часть). */
  docEditorInitConfig: R7DocEditorInitConfig
  /** Browser JWT для DocsAPI.DocEditor (если middleware включил подпись). */
  token?: string
}

/** Состояние синхронизации staging на middleware (WUI-316 / WUI-346). */
export type OfficeAttachmentSyncState = 'syncing' | 'ready' | 'stale' | 'error'

const OFFICE_SYNC_STATES: ReadonlySet<string> = new Set([
  'syncing',
  'ready',
  'stale',
  'error',
])

const DS_STATUS_MIN = 1
const DS_STATUS_MAX = 7

/** `POST /platform/office-editor/prepare` — temp file; для нового вложения — part `file`. */
export async function prepareOfficeAttachmentEditor(
  request: Record<string, unknown>,
  file?: File | null,
  options?: { signal?: AbortSignal },
): Promise<OfficeEditorPrepareResult> {
  logR7AttachmentPrepareDebug('prepare-request', {
    ...snapshotAttachmentPrepareRequest(request),
    hasFilePart: file instanceof File,
    fileSize: file instanceof File ? file.size : null,
  })

  const formData = new FormData()
  formData.append(
    'request',
    new Blob([JSON.stringify(request)], { type: 'application/json' }),
  )
  if (file instanceof File) {
    formData.append('file', file, file.name)
  }

  const response = await axiosInstance({
    ...api.platform.officeEditorPrepare,
    data: formData,
    headers: {
      Accept: 'application/json',
    },
    signal: options?.signal,
  })

  const parsed = parseOfficeEditorPrepareResponse(response.data)
  if (!parsed) {
    throw new Error(
      'Ответ office-editor/prepare не содержит documentKey, documentUrl, callbackUrl, storage_uri или documentSessionId.',
    )
  }
  const normalized = normalizeOfficeEditorPrepareUrls({
    documentUrl: parsed.documentUrl,
    callbackUrl: parsed.callbackUrl,
  })
  if (normalized.urlWarning) {
    console.warn('[R7 attachment]', normalized.urlWarning)
  }
  const result = {
    ...parsed,
    documentUrl: normalized.documentUrl,
    callbackUrl: normalized.callbackUrl,
  }
  logR7AttachmentPrepareDebug('prepare-response', {
    request: snapshotAttachmentPrepareRequest(request),
    response: result,
  })
  return result
}

export function unwrapOfficeEventArg(raw: unknown): unknown {
  if (raw && typeof raw === 'object' && 'value' in raw) {
    return (raw as { value: unknown }).value
  }
  return raw
}

/** WS `OfficeCallbackHandler.statusChanged` и зеркало syncState response (WUI-346 §6). */
export type OfficeCallbackStatusEvent = {
  elementId: number
  syncState: OfficeAttachmentSyncState
  dsStatus: number | null
  documentSessionId: string
  documentKey: string | null
  storageUri: string | null
  documentUrl: string | null
}

/** Минимальные поля WS / лога `OfficeCallbackHandler.statusChanged`. */
export type OfficeCallbackEventInput = {
  objectId?: string | number | null
  object_id?: string | number | null
  args?: unknown[]
}

function resolveElementIdFromEvent(evt: OfficeCallbackEventInput): number {
  const objectIdRaw = evt.objectId ?? evt.object_id
  const elementId = objectIdRaw != null && objectIdRaw !== '' ? Number(objectIdRaw) : NaN
  return Number.isFinite(elementId) && elementId > 0 ? elementId : NaN
}

function parseUriLike(raw: unknown): string | null {
  const str = raw != null ? String(raw).trim() : ''
  if (!str) return null
  if (str.includes('://') || str.startsWith('file:')) return str
  return null
}

function parseSyncStateArg(raw: unknown): OfficeAttachmentSyncState | null {
  const value = String(unwrapOfficeEventArg(raw) ?? '').trim().toLowerCase()
  return OFFICE_SYNC_STATES.has(value) ? (value as OfficeAttachmentSyncState) : null
}

function parseDsStatusArg(raw: unknown): number | null {
  const value = Number(unwrapOfficeEventArg(raw))
  if (!Number.isFinite(value) || value < DS_STATUS_MIN || value > DS_STATUS_MAX) return null
  return value
}

/** `syncState: ready` + DS save (`2` / `6`). */
export function isOfficeCallbackSaveReady(event: OfficeCallbackStatusEvent): boolean {
  return event.syncState === 'ready' && (event.dsStatus === 2 || event.dsStatus === 6)
}

/** `syncState: ready` + DS close без изменений (`4`). */
export function isOfficeCallbackNoChanges(event: OfficeCallbackStatusEvent): boolean {
  return event.syncState === 'ready' && event.dsStatus === 4
}

/** Terminal error от DS или middleware. `stale`/`syncing` — in-flight, не abort. */
export function isOfficeCallbackTerminalError(event: OfficeCallbackStatusEvent): boolean {
  if (event.dsStatus === 3 || event.dsStatus === 7) return true
  return event.syncState === 'error'
}

/**
 * WUI-346 / R12: `[syncState, dsStatus, documentSessionId, documentKey, storage_uri, documentUrl?]`.
 * `documentUrl` не передан → без изменений (web берёт из снимка); обязателен только в ответе prepare.
 */
function parseMiddlewareOfficeCallbackEvent(
  evt: OfficeCallbackEventInput,
  args: unknown[],
): OfficeCallbackStatusEvent | null {
  if (args.length < 5) return null

  const elementId = resolveElementIdFromEvent(evt)
  if (!Number.isFinite(elementId) || elementId <= 0) return null

  const syncState = parseSyncStateArg(args[0])
  if (!syncState) return null

  const dsStatus = parseDsStatusArg(args[1])
  const documentSessionId = String(unwrapOfficeEventArg(args[2]) ?? '').trim()
  if (!documentSessionId) return null

  const documentKey = String(unwrapOfficeEventArg(args[3]) ?? '').trim() || null
  const storageUri = parseUriLike(unwrapOfficeEventArg(args[4]))
  const documentUrlRaw = args.length > 5 ? unwrapOfficeEventArg(args[5]) : null
  const documentUrl =
    documentUrlRaw != null && String(documentUrlRaw).trim() !== ''
      ? String(documentUrlRaw).trim()
      : null

  return {
    elementId,
    syncState,
    dsStatus,
    documentSessionId,
    documentKey,
    storageUri,
    documentUrl,
  }
}

/** Разбор WS `OfficeCallbackHandler.statusChanged` (5–6 args, WUI-346). */
export function parseOfficeCallbackEvent(
  evt: OfficeCallbackEventInput,
): OfficeCallbackStatusEvent | null {
  const args = evt.args ?? []
  if (!args.length) return null
  return parseMiddlewareOfficeCallbackEvent(evt, args)
}

/** Ответ `POST /platform/office-editor/document-sync-state`. */
export type OfficeAttachmentDocumentSyncState = {
  syncState: OfficeAttachmentSyncState
  dsStatus?: number
  updatedAt?: number
  message?: string
  documentSessionId?: string
  documentKey?: string
  storageUri?: string
  documentUrl?: string
  generation?: number
  dsDirty?: boolean
}

/** `POST /platform/office-editor/document-sync-state`. */
export async function fetchOfficeAttachmentDocumentSyncState(request: {
  elementId: number
  session: PlatformFileSessionContext
  documentSessionId: string
}): Promise<OfficeAttachmentDocumentSyncState> {
  const documentSessionId = String(request.documentSessionId ?? '').trim()
  if (!documentSessionId) {
    throw new Error('document-sync-state: нужен documentSessionId')
  }

  const body = buildAttachmentOfficeEditorDocumentRequest({
    elementId: request.elementId,
    session: request.session,
    documentSessionId,
  })

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 5000)
  try {
    const response = await axiosInstance({
      ...api.platform.officeEditorDocumentSyncState,
      data: body,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    })

    const parsed = parseOfficeEditorDocumentSyncStateResponse(response.data)
    if (!parsed) {
      throw new Error('Ответ document-sync-state не содержит syncState: syncing|ready|stale|error')
    }

    return {
      syncState: parsed.syncState,
      dsStatus: parsed.dsStatus,
      updatedAt: parsed.updatedAt,
      message: parsed.message,
      documentSessionId: parsed.documentSessionId,
      documentKey: parsed.documentKey,
      storageUri: parsed.storageUri,
      documentUrl: parsed.documentUrl,
      generation: parsed.generation,
      dsDirty: parsed.dsDirty,
    }
  } finally {
    clearTimeout(timer)
  }
}

/** `POST /platform/office-editor/forcesave` → DS Command Service `{c:forcesave,key}`. */
export async function fetchOfficeAttachmentForceSave(request: {
  elementId: number
  session: PlatformFileSessionContext
  documentSessionId: string
}): Promise<void> {
  const documentSessionId = String(request.documentSessionId ?? '').trim()
  if (!documentSessionId) {
    throw new Error('forcesave: нужен documentSessionId')
  }

  const body = buildAttachmentOfficeEditorDocumentRequest({
    elementId: request.elementId,
    session: request.session,
    documentSessionId,
  })

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 5000)
  try {
    await axiosInstance({
      ...api.platform.officeEditorForceSave,
      data: body,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    })
  } finally {
    clearTimeout(timer)
  }
}

/** Первая строка developerMessage middle, без stack. */
export function formatOfficeEditorForceSaveError(error: unknown): string {
  if (error instanceof ActionError) {
    const detail = String(error.developerMessage ?? '').trim().split('\n')[0] ?? ''
    if (detail && detail.length < 400) return detail
    return error.message || 'Command Service forcesave failed'
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim()
  }
  return 'Command Service forcesave failed'
}

/** Прерывает незавершённый save-цикл (кнопка «Отменить» при `syncState: syncing`). */
export async function discardOfficeAttachmentDocument(request: {
  elementId: number
  session: PlatformFileSessionContext
  documentSessionId: string
}): Promise<void> {
  const documentSessionId = String(request.documentSessionId ?? '').trim()
  if (!documentSessionId) {
    throw new Error('discard: нужен documentSessionId')
  }

  const body = buildAttachmentOfficeEditorDocumentRequest({
    elementId: request.elementId,
    session: request.session,
    documentSessionId,
  })

  await axiosInstance({
    ...api.platform.officeEditorDiscard,
    data: body,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
}
